import {getValidProp} from '../../../../../utils/cleanValidateStringPropValue';
import constants from './constants';
import libConstants from '../../constants';
import CCFormDropdownOpenCloseControl from './_OpenCloseControl';
import computeClassName from "../../../../../utils/computeClassName";
import CCFormDropdownOptions from "./_Options";
import useClickOutside from "../../../../../hooks/ui/useClickOutside";
import {useRef} from "react";

const propsDefaults = Object.freeze({
  size: constants.SIZE_DEFAULT,
  isInline: false,
  isOpened: false,
  isProcessingInProgress: false,
  hasError: false,
  openFn: null,
  openFnArgs: undefined,
  openTitle: 'Open dropdown',
  closeFn: null,
  closeFnArgs: undefined,
  closeTitle: 'Close dropdown',
  isDisabled: false,
  placeholderText: '',
  optionList: [],
  optionKeyLabel: constants.OPTION_KEY_LABEL,
  optionKeyValue: constants.OPTION_KEY_VALUE,
  optionKeyIsDisabled: constants.OPTION_KEY_IS_DISABLED,
  optionKeyIsGroup: constants.OPTION_KEY_IS_GROUP,
  optionKeyIsSelected: constants.OPTION_KEY_IS_SELECTED,
  allowMultipleSelection: false,
  multipleSelectionText: '$count selected',
  replaceCountInMultipleSelectionText: true,
  countKeyword: '$count',
  multipleSelectionTextShownIfCountAbove: 1,
  selectedOptionSeparator: ', ',
  optionSelectFn: null,
  optionSelectFnArgs: undefined,
  optionUnselectFn: null,
  optionUnselectArgs: undefined,
  isLTR: true,
  isFilterable: false,
  filterPlaceholderText: 'Filter',
  filterNoMatchesFoundText: 'No matches found.',
});


function getValidSize(propsSize) {
  return getValidProp(
    propsSize,
    'size',
    constants.SIZE_DEFAULT,
    constants.SIZES,
    constants.COMPONENT_NAME,
    libConstants.LIB_DEBUG_LOGGER,
  );
}

function getComputedClassName(propsClassName, isInline, size) {
  return computeClassName(propsClassName, `${constants.CLASSNAME_BASE} ${constants.CLASSNAME_IS_INLINE[isInline]} ${constants.CLASSNAME_SIZE[size]}`);
}

function getSelectedOptions(options, optionKeyIsSelected) {
  return options.filter((option) => option[optionKeyIsSelected]);
}

export default function CCFormDropdown(
  {
    size = propsDefaults.size,
    isInline = propsDefaults.isInline,
    isOpened = propsDefaults.isOpened,
    isDisabled = propsDefaults.isDisabled,
    isProcessingInProgress = propsDefaults.isProcessingInProgress,
    hasError = propsDefaults.hasError,
    openFn = propsDefaults.openFn,
    openFnArgs = propsDefaults.openFnArgs,
    openTitle = propsDefaults.openTitle,
    closeFn = propsDefaults.closeFn,
    closeFnArgs = propsDefaults.closeFnArgs,
    closeTitle = propsDefaults.closeTitle,
    placeholderText = propsDefaults.placeholderText,
    allowMultipleSelection = propsDefaults.allowMultipleSelection,
    multipleSelectionText = propsDefaults.multipleSelectionText,
    replaceCountInMultipleSelectionText = propsDefaults.replaceCountInMultipleSelectionText,
    countKeyword= propsDefaults.countKeyword,
    multipleSelectionTextShownIfCountAbove = propsDefaults.multipleSelectionTextShownIfCountAbove,
    selectedOptionSeparator = propsDefaults.selectedOptionSeparator,
    optionList = propsDefaults.optionList,
    optionKeyLabel = propsDefaults.optionKeyLabel,
    optionKeyValue = propsDefaults.optionKeyValue,
    optionKeyIsDisabled = propsDefaults.optionKeyIsDisabled,
    optionKeyIsGroup = propsDefaults.optionKeyIsGroup,
    optionKeyIsSelected = propsDefaults.optionKeyIsSelected,
    optionSelectFn = propsDefaults.optionSelectFn,
    optionSelectFnArgs = propsDefaults.optionSelectFnArgs,
    optionUnselectFn = propsDefaults.optionUnselectFn,
    optionUnselectArgs = propsDefaults.optionUnselectArgs,
    isLTR= propsDefaults.isLTR,
    isFilterable = propsDefaults.isFilterable,
    filterPlaceholderText = propsDefaults.filterPlaceholderText,
    filterNoMatchesFoundText = propsDefaults.filterNoMatchesFoundText,
    className = ''
  }) {
  const dropdownRef = useRef();

  useClickOutside(dropdownRef, () => {
    if (isOpened) {
      if (!!closeFn && 'function' === typeof closeFn) {
        if (undefined === closeFnArgs) { closeFn(); }
        else { closeFn(closeFnArgs) }
      }
    }
  });

  const validSize = getValidSize(size);
  const computedClassName = getComputedClassName(className, isInline, validSize);
  const computedSelectedOptions = getSelectedOptions(optionList, optionKeyIsSelected);

  return (
    <div className={computedClassName}
         ref={dropdownRef}>
      <CCFormDropdownOpenCloseControl dropdownSize={validSize}
                                      dropdownIsOpened={isOpened}
                                      dropdownIsDisabled={isDisabled}
                                      dropdownIsProcessingInProgress={isProcessingInProgress}
                                      dropdownHasError={hasError}
                                      dropdownOpenTitle={openTitle}
                                      dropdownOpenFn={openFn}
                                      dropdownOpenFnArgs={openFnArgs}
                                      dropdownCloseTitle={closeTitle}
                                      dropdownCloseFn={closeFn}
                                      dropdownCloseFnArgs={closeFnArgs}
                                      dropdownSelectedOptions={computedSelectedOptions}
                                      dropdownIsLTR={isLTR}
                                      dropdownPlaceholderText={placeholderText}
                                      dropdownMultipleSelectionText={multipleSelectionText}
                                      dropdownReplaceCountInMultipleSelectionText={replaceCountInMultipleSelectionText}
                                      dropdownCountKeyword={countKeyword}
                                      dropdownMultipleSelectionTextShownIfCountAbove={multipleSelectionTextShownIfCountAbove}
                                      dropdownSelectedOptionSeparator={selectedOptionSeparator}
                                      dropdownOptionKeyLabel={optionKeyLabel}/>

      {
        isOpened &&
        <CCFormDropdownOptions
          dropdownSize={size}
          dropdownAllowMultipleSelection={allowMultipleSelection}
          dropdownOptionList={optionList}
          dropdownOptionKeyLabel={optionKeyLabel}
          dropdownOptionKeyValue={optionKeyValue}
          dropdownOptionKeyIsDisabled={optionKeyIsDisabled}
          dropdownOptionKeyIsGroup={optionKeyIsGroup}
          dropdownOptionKeyIsSelected={optionKeyIsSelected}
          dropdownOptionSelectFn={optionSelectFn}
          dropdownOptionSelectFnArgs={optionSelectFnArgs}
          dropdownOptionUnselectFn={optionUnselectFn}
          dropdownOptionUnselectArgs={optionUnselectArgs}
          dropdownIsLTR={isLTR}
          dropdownIsFilterable={isFilterable}
          dropdownFilterPlaceholderText={filterPlaceholderText}
          dropdownFilterNoMatchesFoundText={filterNoMatchesFoundText}/>
      }
    </div>
  );
}

//        <div className="absolute w-full top-100 bg-cc-white border border-cc-accent shadow-cc-lg rounded-b-lg -mt-px">
//           <div className="w-full h-px bg-cc-gray-05 -mt-px"/>
//           opened
//         </div>

//      <CCFormDropdownOptions dropdownAllowMultipleSelection={allowMultipleSelection}
//                              dropdownOptionsTag={optionsTag}
//                              dropdownOptionTag={optionTag}
//                              dropdownOptionList={optionList}
//                              dropdownOptionKeyLabel={optionKeyLabel}
//                              dropdownOptionKeyValue={optionKeyValue}
//                              dropdownOptionKeyIsSelectable={optionKeyIsSelectable}
//                              dropdownOptionKeyIsSelected={optionKeyIsSelected}
//                              dropdownOptionSelectFn={optionSelectFn}
//                              dropdownOptionSelectFnArgs={optionSelectFnArgs}
//                              dropdownOptionUnselectFn={optionUnselectFn}
//                              dropdownOptionUnselectArg={optionUnselectArg}
//                              dropdownIsLTR={isLTR}/>
